import React, { memo, useMemo, useState } from 'react';
import {
  Box,
  Divider,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { viewportGutterMaxWidth } from '../constants/layout';

export interface PickerItem {
  id: string;
  title: string;
  description?: string;
  /** What picking this row does, shown on the right: 'Run', 'Open'. */
  action?: string;
  /**
   * False for a row that is an act rather than one of the choices. Without it such a row drew an
   * unchecked radio in a single-choice list, which reads as an option nobody has picked.
   */
  selectable?: boolean;
}

interface Props {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** One line above the list saying how this kind of thing behaves. */
  caption: string;
  items: PickerItem[];
  onPick: (id: string) => void;
  /** Draws a radio per row and marks this one. Omitted, the rows are plain actions. */
  selectedId?: string | null;
  /** A last row that clears the choice, for a single-choice list. */
  clearLabel?: string;
  onClear?: () => void;
  emptyText: string;
  /** Rows from which a search box is worth its space. */
  searchFrom?: number;
}

// The one list the chat controls open, whatever they are listing. Playbooks and workflows are
// different things and behave differently, but choosing one from a list is the same act, and
// before this each control drew its own list with its own spacing, its own secondary line and no
// way to search either.
//
// Search appears only once a list is long enough to need it: a filter over four rows is a control
// that costs more attention than it saves.
const PickerMenu = memo<Props>(({
  anchorEl,
  onClose,
  caption,
  items,
  onPick,
  selectedId,
  clearLabel,
  onClear,
  emptyText,
  searchFrom = 6,
}) => {
  const [query, setQuery] = useState('');
  const selectable = selectedId !== undefined;
  const searchable = items.length >= searchFrom;

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(needle)
        || (item.description ?? '').toLowerCase().includes(needle),
    );
  }, [items, query]);

  const close = () => {
    onClose();
    setQuery('');
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={close}
      slotProps={{ paper: { sx: { maxWidth: (theme) => `min(420px, ${viewportGutterMaxWidth(theme)})`, minWidth: 300 } } }}
    >
      <Typography
        variant="caption"
        sx={{ px: 2, pt: 1, pb: searchable ? 0.5 : 1, display: 'block', color: 'text.secondary' }}
      >
        {caption}
      </Typography>

      {searchable && (
        <Box sx={{ px: 2, pb: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={query}
            placeholder="Filter"
            inputProps={{ 'aria-label': 'Filter the list' }}
            onChange={(e) => setQuery(e.target.value)}
            // Typing inside a Menu otherwise moves the highlight to a row starting with that
            // letter and steals the keystroke from the field.
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {shown.length === 0 && (
        <MenuItem disabled>
          <ListItemText primary={query ? 'Nothing matches that' : emptyText} />
        </MenuItem>
      )}

      {shown.map((item) => (
        <MenuItem
          key={item.id}
          onClick={() => {
            close();
            onPick(item.id);
          }}
        >
          {selectable
            && (item.selectable === false ? (
              <Box sx={{ width: 34 }} />
            ) : (
              <Radio size="small" checked={item.id === selectedId} sx={{ mr: 1 }} />
            ))}
          <ListItemText
            primary={item.title}
            secondary={item.description}
            slotProps={{ secondary: { sx: { whiteSpace: 'normal' } } }}
          />
          {item.action && (
            <Typography variant="caption" sx={{ pl: 2, color: 'text.secondary' }}>
              {item.action}
            </Typography>
          )}
        </MenuItem>
      ))}

      {clearLabel && onClear && <Divider />}
      {clearLabel && onClear && (
        <MenuItem
          onClick={() => {
            close();
            onClear();
          }}
        >
          {selectable && <Radio size="small" checked={!selectedId} sx={{ mr: 1 }} />}
          <ListItemText primary={clearLabel} />
        </MenuItem>
      )}
    </Menu>
  );
});

PickerMenu.displayName = 'PickerMenu';

export default PickerMenu;
